import { TFunction } from 'i18next'
import { ActorSelectOption, ProgramStatus, ProgramType } from '../../../swagger'
import {
  isWithinDateRange,
  createSafeDate,
  isFirstDateStringBeforeSecond,
} from '../../../utils/dateUtility'
import { reinterpretYearMonthDayAsLocalTime } from '../../../utils/reinterpretYearMonthDayAsLocalTime'
import DropdownSelectOptions from '../../Interfaces/DropdownSelectOptions'

export const isWithinProgramDateRange = (
  semesterDateString: string,
  minimumSemesterDate: Date,
  maximumSemesterDate: Date
): { afterMin: boolean; beforeMax: boolean } => {
  return {
    afterMin: !isFirstDateStringBeforeSecond(
      createSafeDate(semesterDateString),
      minimumSemesterDate
    ),
    beforeMax: !isFirstDateStringBeforeSecond(
      maximumSemesterDate,
      createSafeDate(semesterDateString)
    ),
  }
}

/**
 *
 * @param {ActorSelectOption[]} asoList
 * @param filterOptions
 * is used to filter directors and tutors and conditionally
 * exclude those which role has expired or is not applicable
 */
export const filterActorSelectOption = (
  asoList: ActorSelectOption[],
  filterOptions: {
    type: ProgramType
    firstSemesterStartDate: string
    academicYearEndDate?: string
    communityKey?: number
    directorActorId?: number
    removeExpired?: boolean
    removeNotApplicable?: boolean
    isTutor?: boolean
  }
): Array<
  ActorSelectOption & {
    expired?: boolean
    applicableToResourceType?: boolean
  }
> => {
  const {
    type,
    removeExpired = true,
    removeNotApplicable = true,
    isTutor = false,
    firstSemesterStartDate,
    academicYearEndDate: academicYearEndDateString,
    directorActorId,
  } = filterOptions

  const today = reinterpretYearMonthDayAsLocalTime(new Date(), true)
  // iOS cannot create a date from string in 'MM-DD-YYYY' format
  const [, , year] = firstSemesterStartDate.split('-').map(Number)
  const [month, day] = firstSemesterStartDate.split('-').map(Number)
  const firstSemesterStartDateReinterpreted =
    reinterpretYearMonthDayAsLocalTime(new Date(year, month - 1, day), true)

  // Academic year end date comes from the backend (semesterOneStartDate + ACADEMIC_YEAR_LENGTH)
  const academicYearEndDate = academicYearEndDateString
    ? reinterpretYearMonthDayAsLocalTime(
        new Date(academicYearEndDateString),
        true
      )
    : undefined

  return asoList
    .map((actor) => {
      const reinterpretValidFrom = reinterpretYearMonthDayAsLocalTime(
        actor.validFrom,
        true
      )

      const reinterpretValidTo = reinterpretYearMonthDayAsLocalTime(
        actor.validTo,
        true
      )

      let expired = false
      if (isTutor) {
        expired = !isWithinDateRange(
          { start: reinterpretValidFrom, end: actor.validTo },
          today
        )
      } else {
        // For directors: show if the role dates overlap with the program dates
        // AND the role is not expired (validTo >= today).

        const roleEndNotBeforeProgramStart = !isFirstDateStringBeforeSecond(
          reinterpretValidTo,
          firstSemesterStartDateReinterpreted
        )
        const roleStartNotAfterProgramEnd = academicYearEndDate
          ? !isFirstDateStringBeforeSecond(
              academicYearEndDate,
              reinterpretValidFrom
            )
          : true
        const roleOverlapsProgram =
          roleEndNotBeforeProgramStart && roleStartNotAfterProgramEnd

        const validToNotBeforeToday = !isFirstDateStringBeforeSecond(
          reinterpretValidTo,
          today
        )

        expired = !(roleOverlapsProgram && validToNotBeforeToday)
      }
      return {
        ...actor,
        expired,
        applicableToResourceType: actor.applicableToResourceTypes?.some(
          (programType) => programType === type
        ),
      }
    })
    .filter((actor) => {
      if (removeExpired && actor.expired) return false
      if (removeNotApplicable && !actor.applicableToResourceType) return false
      if (
        directorActorId &&
        directorActorId > 0 &&
        isTutor &&
        !actor.applicableToResourceKeys?.includes(directorActorId)
      )
        return false
      return true
    })
}

export const getDaysOfTheWeek = (t: TFunction): DropdownSelectOptions[] => [
  {
    id: 0,
    label: `${t(
      'Programs.ProgramFormCard.DropDownMenuOption.Sunday',
      'Sunday'
    )}`,
  },
  {
    id: 1,
    label: `${t(
      'Programs.ProgramFormCard.DropDownMenuOption.Monday',
      'Monday'
    )}`,
  },
  {
    id: 2,
    label: `${t(
      'Programs.ProgramFormCard.DropDownMenuOption.Tuesday',
      'Tuesday'
    )}`,
  },
  {
    id: 3,
    label: `${t(
      'Programs.ProgramFormCard.DropDownMenuOption.Wednesday',
      'Wednesday'
    )}`,
  },
  {
    id: 4,
    label: `${t(
      'Programs.ProgramFormCard.DropDownMenuOption.Thursday',
      'Thursday'
    )}`,
  },
  {
    id: 5,
    label: `${t(
      'Programs.ProgramFormCard.DropDownMenuOption.Friday',
      'Friday'
    )}`,
  },
  {
    id: 6,
    label: `${t(
      'Programs.ProgramFormCard.DropDownMenuOption.Saturday',
      'Saturday'
    )}`,
  },
]

export const getProgramStatusLabel = (
  t: TFunction
): Record<ProgramStatus, string> => ({
  [ProgramStatus.Draft]: t('Card.Status.Draft', 'Draft'),
  [ProgramStatus.Published]: t('Card.Status.Published', 'Published'),
  [ProgramStatus.Complete]: t('Card.Status.Complete', 'Complete'),
  [ProgramStatus.Archived]: t('Card.Status.Archived', 'Archived'),
})

export const calculateFourMonthsFromToday = (): Date => {
  const today = new Date()
  const date = new Date()
  date.setMonth(today.getMonth() + 4)

  return date
}
